import { Decimal, type DecimalValue } from "./Decimal";

export type MoneyValue = Readonly<{ value: DecimalValue; currency: "USD" }>;
function validate(money: MoneyValue): MoneyValue {
  if (money.currency !== "USD") throw new Error("Unsupported currency");
  return { value: Decimal.validate(money.value), currency: money.currency };
}
function pair(left: MoneyValue, right: MoneyValue) {
  return [validate(left), validate(right)] as const;
}
const zero = Decimal.parse("0");

export const Money = {
  validate,
  create: (value: DecimalValue): MoneyValue => validate({ value, currency: "USD" }),
  add: (left: MoneyValue, right: MoneyValue): MoneyValue => {
    const [a, b] = pair(left, right);
    return Money.create(Decimal.add(a.value, b.value));
  },
  subtract: (left: MoneyValue, right: MoneyValue): MoneyValue => {
    const [a, b] = pair(left, right);
    return Money.create(Decimal.subtract(a.value, b.value));
  },
  compare: (left: MoneyValue, right: MoneyValue) => {
    const [a, b] = pair(left, right);
    return Decimal.compare(a.value, b.value);
  },
  isPositive: (money: MoneyValue): boolean => Decimal.compare(validate(money).value, zero) > 0,
  isNegative: (money: MoneyValue): boolean => Decimal.compare(validate(money).value, zero) < 0,
  isZero: (money: MoneyValue): boolean => Decimal.compare(validate(money).value, zero) === 0,
  abs: (money: MoneyValue): MoneyValue => {
    const valid = validate(money);
    return Money.isNegative(valid) ? Money.create(Decimal.subtract(zero, valid.value)) : valid;
  },
  /** Preserves precision and displays at least two places; never rounds. */
  format: (money: MoneyValue): string => {
    const text = Decimal.toText(validate(money).value);
    const negative = text.startsWith("-");
    const [whole, fraction = ""] = text.replace("-", "").split(".");
    return `${negative ? "-" : ""}$${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${fraction.padEnd(2, "0")}`;
  }
};
